import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
const { encodeBase64, decodeBase64 } = naclUtil;
// Re-export base64 helpers so consumers don't need to depend on tweetnacl-util directly
export { encodeBase64, decodeBase64 };
/**
 * Encode a UTF-8 string to a Uint8Array.
 * Named to match the intuitive TextEncoder mental model (string → bytes).
 */
export function encodeUTF8(s) {
    return new TextEncoder().encode(s);
}
/**
 * Decode a Uint8Array of UTF-8 bytes to a string.
 * Named to match the intuitive TextDecoder mental model (bytes → string).
 */
export function decodeUTF8(bytes) {
    return new TextDecoder().decode(bytes);
}
/** Generate a new X25519 key pair. Returns base64-encoded keys. */
export function generateKeyPair() {
    const kp = nacl.box.keyPair();
    return {
        publicKey: encodeBase64(kp.publicKey),
        secretKey: encodeBase64(kp.secretKey),
    };
}
/** Generate a random 32-byte symmetric channel key. Returns base64. */
export function generateChannelKey() {
    return encodeBase64(nacl.randomBytes(32));
}
/** Generate a random 32-byte PBKDF2 salt. Returns base64. */
export function generateSalt() {
    return encodeBase64(nacl.randomBytes(32));
}
/**
 * Derive a 32-byte Key Encryption Key from a password + salt using PBKDF2-SHA256.
 * 600,000 iterations per OWASP recommendation.
 * Requires globalThis.crypto.subtle (Node 20+, browsers, MV3 service workers).
 */
export async function deriveKEK(password, saltB64) {
    // TS 5.7 typed Uint8Array as Uint8Array<ArrayBufferLike>, and Web
    // Crypto's BufferSource wants a non-shared ArrayBuffer. Copying into a
    // fresh Uint8Array narrows the backing buffer to ArrayBuffer and works
    // in every lib target (no DOM-only BufferSource cast needed).
    const passwordBytes = new Uint8Array(encodeUTF8(password));
    const saltBytes = new Uint8Array(decodeBase64(saltB64));
    const keyMaterial = await globalThis.crypto.subtle.importKey('raw', passwordBytes, 'PBKDF2', false, ['deriveBits']);
    const bits = await globalThis.crypto.subtle.deriveBits({
        name: 'PBKDF2',
        salt: saltBytes,
        iterations: 600_000,
        hash: 'SHA-256',
    }, keyMaterial, 256);
    return new Uint8Array(bits);
}
/**
 * Encrypt a raw private key Uint8Array with a KEK using XSalsa20-Poly1305.
 * Returns base64(nonce[24] + ciphertext).
 */
export function encryptPrivateKey(secretKeyBytes, kek) {
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
    const ciphertext = nacl.secretbox(secretKeyBytes, nonce, kek);
    const out = new Uint8Array(nonce.length + ciphertext.length);
    out.set(nonce);
    out.set(ciphertext, nonce.length);
    return encodeBase64(out);
}
/**
 * Decrypt a private key encrypted with encryptPrivateKey.
 * Returns null if the KEK is wrong or data is corrupt.
 */
export function decryptPrivateKey(encryptedB64, kek) {
    let data;
    try {
        data = decodeBase64(encryptedB64);
    }
    catch {
        return null;
    }
    if (data.length < nacl.secretbox.nonceLength + nacl.secretbox.overheadLength)
        return null;
    const nonce = data.slice(0, nacl.secretbox.nonceLength);
    const ciphertext = data.slice(nacl.secretbox.nonceLength);
    return nacl.secretbox.open(ciphertext, nonce, kek);
}
/**
 * Sealed-box encrypt: encrypt plaintext for a recipient's public key using an ephemeral
 * X25519 key pair. The ephemeral public key is prepended so the recipient can decrypt
 * without needing to know who sent it.
 *
 * Output layout: ephemeralPublicKey[32] + nonce[24] + ciphertext
 * Returns base64.
 */
export function sealEncrypt(plaintext, recipientPublicKeyB64) {
    const recipientPublicKey = decodeBase64(recipientPublicKeyB64);
    const eph = nacl.box.keyPair();
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const sharedKey = nacl.box.before(recipientPublicKey, eph.secretKey);
    const ciphertext = nacl.box.after(plaintext, nonce, sharedKey);
    const pkLen = nacl.box.publicKeyLength;
    const out = new Uint8Array(pkLen + nonce.length + ciphertext.length);
    out.set(eph.publicKey, 0);
    out.set(nonce, pkLen);
    out.set(ciphertext, pkLen + nonce.length);
    return encodeBase64(out);
}
/**
 * Decrypt a sealed-box encrypted value using the recipient's secret key.
 * Returns null if decryption fails.
 */
export function sealDecrypt(sealedB64, recipientSecretKeyB64) {
    try {
        const data = decodeBase64(sealedB64);
        const pkLen = nacl.box.publicKeyLength;
        if (data.length < pkLen + nacl.box.nonceLength + nacl.box.overheadLength)
            return null;
        const ephPublicKey = data.slice(0, pkLen);
        const nonce = data.slice(pkLen, pkLen + nacl.box.nonceLength);
        const ciphertext = data.slice(pkLen + nacl.box.nonceLength);
        const sharedKey = nacl.box.before(ephPublicKey, decodeBase64(recipientSecretKeyB64));
        return nacl.box.open.after(ciphertext, nonce, sharedKey);
    }
    catch {
        return null;
    }
}
/**
 * Encrypt a channel key (base64) for a member using sealed-box.
 * Returns base64 sealed blob.
 */
export function encryptChannelKey(channelKeyB64, recipientPublicKeyB64) {
    return sealEncrypt(decodeBase64(channelKeyB64), recipientPublicKeyB64);
}
/**
 * Decrypt a sealed channel key using own secret key.
 * Returns the channel key as base64, or null on failure.
 */
export function decryptChannelKey(sealedB64, mySecretKeyB64) {
    const keyBytes = sealDecrypt(sealedB64, mySecretKeyB64);
    return keyBytes ? encodeBase64(keyBytes) : null;
}
/**
 * Encrypt a channel message with a symmetric channel key.
 * Returns base64(nonce[24] + ciphertext).
 */
export function encryptMessage(content, channelKeyB64) {
    const channelKey = decodeBase64(channelKeyB64);
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
    const ciphertext = nacl.secretbox(encodeUTF8(content), nonce, channelKey);
    const out = new Uint8Array(nonce.length + ciphertext.length);
    out.set(nonce);
    out.set(ciphertext, nonce.length);
    return encodeBase64(out);
}
/**
 * Decrypt a channel message. Returns null if key is wrong or data is corrupt.
 */
export function decryptMessage(encryptedB64, channelKeyB64) {
    const channelKey = decodeBase64(channelKeyB64);
    let data;
    try {
        data = decodeBase64(encryptedB64);
    }
    catch {
        return null;
    }
    if (data.length < nacl.secretbox.nonceLength + nacl.secretbox.overheadLength)
        return null;
    const nonce = data.slice(0, nacl.secretbox.nonceLength);
    const ciphertext = data.slice(nacl.secretbox.nonceLength);
    const plaintext = nacl.secretbox.open(ciphertext, nonce, channelKey);
    return plaintext ? decodeUTF8(plaintext) : null;
}
/**
 * Encrypt a DM for both recipient and sender (so sender can read their own sent messages).
 * Returns two independent sealed blobs.
 */
export function encryptDM(content, recipientPublicKeyB64, senderPublicKeyB64) {
    const plaintext = encodeUTF8(content);
    return {
        forRecipient: sealEncrypt(plaintext, recipientPublicKeyB64),
        forSender: sealEncrypt(plaintext, senderPublicKeyB64),
    };
}
/**
 * Decrypt a DM blob using the holder's secret key.
 * Pass forRecipient if you're the recipient, forSender if you're the sender.
 */
export function decryptDM(sealedB64, mySecretKeyB64) {
    const plaintext = sealDecrypt(sealedB64, mySecretKeyB64);
    return plaintext ? decodeUTF8(plaintext) : null;
}
/**
 * Auto-decrypt a DM by trying forRecipient then forSender.
 * Use this when you don't know which role you are in the conversation.
 */
export function decryptDMAuto(dmContent, mySecretKeyB64) {
    return (decryptDM(dmContent.forRecipient, mySecretKeyB64) ??
        decryptDM(dmContent.forSender, mySecretKeyB64));
}
/**
 * Encrypt raw bytes (e.g. a file buffer) with a symmetric channel key.
 * Returns base64(nonce[24] + ciphertext).
 * For large files this runs synchronously — callers should await in a
 * non-blocking context (e.g. after a microtask yield).
 */
export function encryptFileBytes(data, channelKeyB64) {
    const channelKey = decodeBase64(channelKeyB64);
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
    const ciphertext = nacl.secretbox(data, nonce, channelKey);
    const out = new Uint8Array(nonce.length + ciphertext.length);
    out.set(nonce);
    out.set(ciphertext, nonce.length);
    return encodeBase64(out);
}
/**
 * Decrypt raw bytes that were encrypted with encryptFileBytes.
 * Returns the original Uint8Array, or null if the key is wrong / data corrupt.
 */
export function decryptFileBytes(encryptedB64, channelKeyB64) {
    const channelKey = decodeBase64(channelKeyB64);
    let data;
    try {
        data = decodeBase64(encryptedB64);
    }
    catch {
        return null;
    }
    if (data.length < nacl.secretbox.nonceLength + nacl.secretbox.overheadLength)
        return null;
    const nonce = data.slice(0, nacl.secretbox.nonceLength);
    const ciphertext = data.slice(nacl.secretbox.nonceLength);
    return nacl.secretbox.open(ciphertext, nonce, channelKey);
}
/**
 * Semantic alias of {@link sealEncrypt} for use at file-bytes call sites.
 *
 * Provides readable intent at upload paths (`sealEncryptFileBytes(bytes, key)`)
 * without changing framing or behaviour. Output is identical to `sealEncrypt`:
 * base64(ephPub[32] + nonce[24] + ciphertext).
 */
export function sealEncryptFileBytes(data, recipientPublicKeyB64) {
    return sealEncrypt(data, recipientPublicKeyB64);
}
/**
 * Semantic alias of {@link sealDecrypt} for use at file-bytes call sites.
 *
 * Returns the original `Uint8Array`, or `null` on any decryption failure
 * (wrong key, truncated payload, tag mismatch, etc.).
 */
export function sealDecryptFileBytes(sealedB64, mySecretKeyB64) {
    return sealDecrypt(sealedB64, mySecretKeyB64);
}
//# sourceMappingURL=crypto.js.map